import { noop } from 'lodash';
import { RevisionSingleActionProps } from 'components/tables/data/revision/types';
import { Loader, Menu, Tooltip } from '@mantine/core';
import { IconSignature } from '@tabler/icons-react';
import useSignAction from 'components/tables/data/revision/actions/hooks/use-sign-action';
import { useSignRevisionModal } from 'components/modals/sign-revision-modal/SignRevisionModalProvider';
import { useApi } from 'api/api-context';
import panic from 'errors/panic';
import { useState } from 'react';
import Button from 'components/Button';
import ActionIcon from 'components/ActionIcon';

/**
 * Action performed by the user who is the technician of the revision.
 */
export default function SignAgainAction({ revision, onStatusChange = noop, kind }: RevisionSingleActionProps) {
  const { signRevision } = useSignAction();
  const { signRevisionModal } = useSignRevisionModal();
  const { getAction } = useApi();
  const [loading, setLoading] = useState(false);

  /**
   * Confirms the redirect to the third party to sign the revision.
   * Fetches all revisions with the same kepMessageUuid and passes all their IDs.
   */
  const confirmSignRevision = async () => {
    let revisionIds = [revision.revisionId];

    // If kepMessageUuid exists, fetch all revisions with the same kepMessageUuid
    if (revision.kepMessageUuid) {
      setLoading(true);
      try {
        const revisionGetListAction = getAction('RevisionList');
        const revisions = await revisionGetListAction({
          query: { filters: { 'kepMessageUuid.eq': revision.kepMessageUuid } },
        });
        revisionIds = revisions.map((rev) => rev.revisionId);
      } catch (error: any) {
        panic(error);
      } finally {
        setLoading(false);
      }
    }

    signRevisionModal({
      revisionIds,
      message:
        revisionIds.length > 1
          ? revision.kind === 'revision'
            ? 'Revízne správy budú opätovne odoslané do systému BrainIT. Pre ich podpísanie budete presmerovaný do ich webového portálu.'
            : 'Formuláre budú opätovne odoslané do systému BrainIT. Pre ich podpísanie budete presmerovaný do ich webového portálu.'
          : revision.kind === 'revision'
          ? 'Revízna správa bude opätovne odoslaná do systému BrainIT. Pre jej podpísanie budete presmerovaný do ich webového portálu.'
          : 'Formulár bude opätovne odoslaný do systému BrainIT. Pre jeho podpísanie budete presmerovaný do ich webového portálu.',
      onSign: ({ deadline }) => signRevision({ revision, deadline, onStatusChange }),
    });
  };

  if (kind === 'action-icon') {
    return (
      <Tooltip label="Znovu podpísať" events={{ hover: true, focus: true, touch: true }}>
        <ActionIcon variant="secondary" size="md" onClick={confirmSignRevision} loading={loading}>
          <IconSignature stroke={1.5} size={24} />
        </ActionIcon>
      </Tooltip>
    );
  }

  if (kind === 'button') {
    return (
      <Button
        size="md"
        onClick={confirmSignRevision}
        leftIcon={<IconSignature stroke={1.5} size={24} />}
        variant="secondary"
        w={188}
        loading={loading}
      >
        Znovu podpísať
      </Button>
    );
  }

  return (
    <Menu.Item
      color="gray.8"
      onClick={confirmSignRevision}
      icon={loading ? <Loader size={24} /> : <IconSignature stroke={1.5} size={24} />}
      disabled={loading}
    >
      Znovu podpísať
    </Menu.Item>
  );
}
